import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DisableSettingsEdit,
  SettingsEditState,
  UnSavedSettingsEdit,
} from '@ops/services';
import {
  AdditionalSocialMediaChannels,
  Company,
  CompanyService,
  EnumService,
  LocalStorageService,
  StubCompany,
} from '@ops/shared-types';
import { Observable, Subscription } from 'rxjs';
// Exporting all form-related options as empty arrays
const ALL_OPTIONS = {
  keyServices: [
    'Marketing',
    'Engineering',
    'Technology',
    'Finance',
    'Healthcare',
    'Education',
    'Retail',
  ],
  jurisdictionsResidencies: [],
  financialYearTerms: [],
  professionalRoles: [
    'Engineer',
    'Designer',
    'Product Manager',
    'Developer',
    'Consultant',
  ],
  projectRoles: ['Team Lead', 'Scrum Master', 'Project Manager', 'QA Lead'],
  businessTypes: [
    'Agency',
    'Co-Work Collective',
    'Corporation',
    'Gallery',
    'Freelance / Independent',
    'Non-Profit / NGO',
    'Partnership',
    'Self-Employed',
    'Small & Medium Business',
    'Startup',
  ],
  creativeIndustrySectors: [
    'Artist, Actor, Talent Agency',
    'Brand Company (Single)',
    'Brand Company (Multi)',
    'Creative Agency',
    'Creative Production Company',
    'Design, Animation, Branding Studio',
    'Digital-First Agency',
    'Event Planning & Production Company',
    'Film & TV Studio',
    'Freelance Professional',
    'Gaming Studio',
    'Independent Creator',
    'Marketing Agency',
    'Media & Publishing Company',
    'Music Production Studio',
    'Photography Studio',
    'Post Production Studio',
    'Production Service Company',
    'Rental & Equipment Company',
    'Streaming Platform',
    'Transportation & Location Company',
    'Virtual / AR Studio',
  ],
  marketDomains: [
    'Art & Culture',
    'Beauty & Make Up',
    'Consumer Goods',
    'Design & Craft',
    'Fashion & Apparel',
    'Film & Narrative',
    'Food & Beverages',
    'Education',
    'Entertainment & Sports',
    'Digital-Only Services',
    'Gaming & Interactive Media',
    'Health & Wellness',
    'Luxury Goods',
    'Media & News',
    'Music & Audio',
    'Mobility & Transportation',
    'Retail & Experience Spaces',
    'Sustainable & Social Solutions',
    'Tech Goods & Services',
    'Travel & Leisure',
    'Other',
  ],
  fieldOfExpertises: [
    'Software Engineering',
    'Graphic Design',
    'Marketing',
    'Data Science',
    'UI/UX Design',
  ],
  workStylePreferences: ['Remote', 'On-site', 'Hybrid'],
  digitalTools: ['Figma', 'Photoshop', 'Slack', 'Trello', 'JIRA'],
  preferredCommunicationMethods: [
    'Email',
    'Phone',
    'Video Calls',
    'Messaging Apps',
  ],
  teamPlays: ['Collaborative', 'Independent', 'Leader', 'Follower'],
  workTools: ['Laptop', 'Tablet', 'Smartphone', 'Workstation'],
  interestsAndInspirations: [
    'Technology',
    'Art',
    'Music',
    'Traveling',
    'Fitness',
  ],
  favoriteArtistsArtworksCultures: [
    'Picasso',
    'Beethoven',
    'Leonardo da Vinci',
    'Van Gogh',
  ],
  leisureActivities: ['Hiking', 'Reading', 'Gaming', 'Yoga', 'Photography'],
  starSigns: [
    'Aries',
    'Taurus',
    'Gemini',
    'Cancer',
    'Leo',
    'Virgo',
    'Libra',
    'Scorpio',
    'Sagittarius',
    'Capricorn',
    'Aquarius',
    'Pisces',
  ],
  personalities: [
    'ISTJ',
    'ENFP',
    'INTJ',
    'INFJ',
    'ENTP',
    'ISFJ',
    'ESFP',
    'ENTJ',
  ],
  zodiacSigns: [
    'Rat',
    'Ox',
    'Tiger',
    'Rabbit',
    'Dragon',
    'Snake',
    'Horse',
    'Goat',
    'Monkey',
    'Rooster',
    'Dog',
    'Pig',
  ],
  ennagrams: [
    'Type 1: The Reformer',
    'Type 2: The Helper',
    'Type 3: The Achiever',
    'Type 4: The Individualist',
    'Type 5: The Investigator',
    'Type 6: The Loyalist',
    'Type 7: The Enthusiast',
    'Type 8: The Challenger',
    'Type 9: The Peacemaker',
  ],
};

// Exporting all form names as empty strings or null values
export const FORM_NAMES = {
  fullName: '',
  username: '',
  selectedGender: '',
  selectedDay: null,
  selectedMonth: null,
  selectedYear: null,
  jurisdictionResidency: '',
  fiscalYearStartMonth: 1,
  fiscalYearStartDay: 1,
  numOfMembers: 0,
  keyService: '',
  professionalRole: '',
  projectRole: '',
  businessType: '',
  creativeIndustrySector: '',
  marketDomain: '',
  fieldOfExpertise: '',
  workStylePreference: '',
  digitalTool: '',
  preferredCommunicationMethod: '',
  teamPlay: '',
  workTool: '',
  interestsAndInspiration: '',
  favoriteArtistArtworkCulture: '',
  leisureActivity: '',
  website: '',
  linkedin: '',
  youtube: '',
  vimeo: '',
  instagram: '',
  tiktok: '',
  imdb: '',
  twitter: '',
  starSign: '',
  personality: '',
  zodiacSign: '',
  ennagram: '',
};

@Component({
  selector: 'ops-organisation-settings',
  templateUrl: './ops-organisation-settings.component.html',
  styleUrl: './ops-organisation-settings.component.scss',
})
export class OpsOrganisationSettingsComponent implements OnInit, OnDestroy {
  editState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getEditState
  );

  saveState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getSaveState
  );

  selectedCompany$;

  selectedCompanyId: string;

  companies;
  companyOptions;
  company: Company;
  selectedCompany: Company = { ...StubCompany };

  errorsTrack = {};

  backgroundImageUrl?: string | ArrayBuffer;

  get organisationLogoStyle() {
    const logoUrl = this.backgroundImageUrl || this.selectedCompany.logoUrl;
    const styleObj = logoUrl ? { backgroundImage: 'url(' + logoUrl + ')' } : {};
    return styleObj;
  }

  constructor(
    private store: Store,
    private companyService: CompanyService,
    private ls: LocalStorageService,
    private enumService: EnumService,
    private element: ElementRef,
    private translateService: TranslateService  
  ) {
    this.selectedCompany$ = this.companyService.selectedCompany$.asObservable();
  }

  subs: Array<Subscription> = [];

  ngOnInit(): void {
    const eState = this.editState$.subscribe((isEditing: any) => {
      this.isPreview = !isEditing;
      this.errorsTrack = {};

      if (this.companies?.length) {
        if (this.ls.getCurrentCompany()?._id) {
          const sComp = this.companies.find(
            (ci) => ci._id === this.ls.getCurrentCompany()._id
          ) as Company;

          this.company = { ...StubCompany, ...sComp };
          this.selectedCompany = { ...StubCompany, ...sComp };
          this.selectedCompany = {
            ...StubCompany,
            ...this.company,
          };

          if (this.company.additionalSocialMediaChannels.length) {
            this.selectedCompany.additionalSocialMediaChannels = [
              ...this.company.additionalSocialMediaChannels,
            ];
          } else {
            this.selectedCompany.additionalSocialMediaChannels = [];
          }
        } else {
          this.selectedCompany = { ...StubCompany };
          this.selectedCompany.additionalSocialMediaChannels = [];
        }
      } else {
        this.selectedCompany = { ...StubCompany };
        this.selectedCompany.additionalSocialMediaChannels = [];
      }
    });

    const sState = this.saveState$.subscribe((saveChanges: any) => {
      if (saveChanges === true) {
        const errorFields = Object.keys(this.errorsTrack);
        if (errorFields.length === 0) {
          this.saveOrgChanges();
          this.store.dispatch(new UnSavedSettingsEdit());
        } else {
          const errorsList = errorFields.map((errorKey) => {
            return (
              '\n' + errorKey.toUpperCase() + ': ' + this.errorsTrack[errorKey]
            );
          });

          const joinMessage = errorsList.join('');

          this.store.dispatch(new UnSavedSettingsEdit());

          window.alert(
            this.translateService.instant('windowAlert.opsOrganisationSettings.invalidFields', { errors: joinMessage })
          );
        }
      }
    });

    this.subs.push(eState);
    this.subs.push(sState);

    this.mainCurrencies = this.enumService
      .getCurrencyOptionsDetailed()
      .map((cop: any) => {
        return { key: cop.name, value: cop.shortName };
      });

    this.companyService
      .getCompaniesDetailed()
      .subscribe((companies: Array<Company>) => {
        this.companies = companies;
        this.companyOptions = companies.map((c) => c.name);

        const sComp = this.companies.find(
          (ci) => ci._id === this.ls.getCurrentCompany()._id
        ) as Company;

        this.company = { ...StubCompany, ...sComp };
        this.selectedCompany = { ...StubCompany, ...sComp };
      });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());

    if (this.saveCompanySub?.unsubscribe) {
      this.saveCompanySub.unsubscribe();
    }
  }

  organizationName = '';
  mainPlaceOfWork = '';
  otherPlacesOfWork = [];
  businessAddress = '';
  mainCurrency = '';
  genderOptions = ['Male', 'Female', 'Other'];
  selectedGender = '';
  dayOptions = Array.from({ length: 28 }, (_, i) => i + 1); // Days 1-31
  monthOptions = Array.from({ length: 12 }, (_, i) => i + 1); // Months 1-12
  yearOptions = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  isPreview?: boolean = true;

  selectedDay: number | null = null;
  selectedMonth: number | null = null;
  selectedYear: number | null = null;
  mainCurrencies = [];

  financialYearTerms = ALL_OPTIONS.financialYearTerms;
  jurisdictionsResidencies = ALL_OPTIONS.jurisdictionsResidencies;
  keyServicesList = ALL_OPTIONS.keyServices;
  professionalRoles = ALL_OPTIONS.professionalRoles;
  businessTypes = ALL_OPTIONS.businessTypes;
  creativeIndustrySectors = ALL_OPTIONS.creativeIndustrySectors;
  marketDomainsList = ALL_OPTIONS.marketDomains;
  fieldOfExpertises = ALL_OPTIONS.fieldOfExpertises;
  workStylePreferences = ALL_OPTIONS.workStylePreferences;

  uniqueId = '';
  taxId = '';
  fiscalYearStartMonth = FORM_NAMES.fiscalYearStartMonth;
  fiscalYearStartDay = FORM_NAMES.fiscalYearStartDay;
  numOfMembers = FORM_NAMES.numOfMembers;
  jurisdictionsResidency = FORM_NAMES.jurisdictionResidency;
  keyService = FORM_NAMES.keyService;
  projectRole = FORM_NAMES.projectRole;
  businessType = FORM_NAMES.businessType;
  creativeIndustrySector = FORM_NAMES.creativeIndustrySector;
  marketDomain = FORM_NAMES.marketDomain;

  website = FORM_NAMES.website;
  linkedin = FORM_NAMES.linkedin;
  youtube = FORM_NAMES.youtube;
  vimeo = FORM_NAMES.vimeo;
  instagram = FORM_NAMES.instagram;
  tiktok = FORM_NAMES.tiktok;
  imdb = FORM_NAMES.imdb;
  twitter = FORM_NAMES.twitter;

  additionalSocialMediaChannels = [];

  saveCompanySub: Subscription;

  saveOrgChanges() {
    const companyId = this.selectedCompany._id + '';

    const keyServicesOverride = Array.isArray(this.selectedCompany.keyServices)
      ? this.selectedCompany.keyServices
      : this.selectedCompany.keyServices
      ? [this.selectedCompany.keyServices]
      : [];

    const companyChanges = {
      ...this.selectedCompany,
      keyServices: keyServicesOverride,
      logo: this.selectedCompany.logoUrl,
    };

    delete companyChanges['baseCurrency'];
    // delete companyChanges['otherPlacesOfWork'];
    delete companyChanges['balance'];
    delete companyChanges['deletedAt'];
    delete companyChanges['id'];
    delete companyChanges['_id'];
    delete companyChanges['id'];
    delete companyChanges['logoUrl'];
    delete companyChanges['createdAt'];
    delete companyChanges['updatedAt'];
    delete companyChanges['__v'];
    delete companyChanges['collaborations']

    if (this.saveCompanySub?.unsubscribe) {
      this.saveCompanySub.unsubscribe();
    }
    this.saveCompanySub = this.companyService
      .updateCompany(companyId, companyChanges)
      .subscribe(
        (companyUpdated: Company) => {
          // this.sessionManager.setCurrentUser(userUpdated);
          this.company = { ...this.company, ...companyUpdated };
          const companiesSet = this.companies.filter(
            (cf) => cf._id !== companyId
          );

          this.ls.setCurrentCompany({
            _id: companyUpdated._id,
            name: companyUpdated.shortName,
            sealUrl: companyUpdated.sealUrl,
          });
          // this.companyService.selectedCompany$.next(selectedCompanyFilter);

          this.companies = [...companiesSet, companyUpdated];
          this.store.dispatch(new DisableSettingsEdit());
        },
        (error) => {
          //    console.log('Error', error);
          if (error?.error?.message) {
            const errorsList = error?.error?.message.split('.');
            const fullMessage = this.translateService.instant('windowAlert.opsOrganisationSettings.updateError', { errors: errorsList.join('.\n') });
            window.alert(fullMessage);
          }
        }
      );
  }

  setFocusOnCreatedChannel() {
    const itemIdToFocus =
      this.selectedCompany.additionalSocialMediaChannels.length - 1;

    if (itemIdToFocus >= 0 && !this.isPreview) {
      const itemId = `ad-channel-${itemIdToFocus}`;

      document.getElementById(itemId).getElementsByTagName('input')[0].focus();
    }
  }

  onAdditionalChannelChange(value: string, channelName: string) {
    const adItem = this.selectedCompany.additionalSocialMediaChannels.find(
      (adItem) => {
        return adItem.name === channelName;
      }
    );
    adItem.url = value;
  }

  removeAdChannel(additionalChannel: AdditionalSocialMediaChannels) {
    const addChannels = [...this.selectedCompany.additionalSocialMediaChannels];
    this.selectedCompany.additionalSocialMediaChannels = [];

    this.selectedCompany.additionalSocialMediaChannels = [
      ...addChannels.filter(
        (adCh) =>
          !(
            adCh.name === additionalChannel.name &&
            adCh.url === additionalChannel.url
          )
      ),
    ];

    this.errorsTrack = {};
  }

  createNewChannel(channelName: string) {
    const exists = this.selectedCompany.additionalSocialMediaChannels.find(
      (chNameItem) => {
        return chNameItem.name === channelName;
      }
    );

    if (exists) {
      const channelName = exists.name[exists.name.length - 1];
      const numValue = isNaN(parseInt(channelName))
        ? ' 1'
        : ' ' + (parseInt(channelName) + 1);
      const channelNameNew = exists.name.split(' ')[0];
      const newValName = channelNameNew + ' ' + numValue;
      return this.createNewChannel(newValName);
    }
    this.selectedCompany.additionalSocialMediaChannels.push({
      name: channelName,
      url: '',
    });

    setTimeout(() => {
      this.setFocusOnCreatedChannel();
    }, 150);
  }

  readSVGFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  convertSVGToImage(svgText, format) {
    return new Promise((resolve) => {
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL(format));
      };

      img.src = url;
    });
  }

  base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  }

  base64ToFile(base64, fileName, mimeType) {
    const blob = this.base64ToBlob(base64, mimeType);
    return new File([blob], fileName, { type: mimeType });
  }

  async uploadFile(event: Event) {
    const allowedFormats = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/svg+xml',
      'image/bmp',
      'image/gif',
      'image/webp',
    ];
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (!allowedFormats.includes(file.type)) {
        window.alert(
          this.translateService.instant('windowAlert.opsOrganisationSettings.invalidFileFormat')
        );
        (event.target as HTMLInputElement).value = '';
        return;
      }

      const imageUrlFinal = [];

      if (file && file.type === 'image/svg+xml') {
        const svgData = await this.readSVGFile(file);
        const imageUrl = await this.convertSVGToImage(svgData, 'image/png'); // Change to 'image/jpeg' for JPEG format
        // document.getElementById('outputImage').src = imageUrl;

        const convertedFile = this.base64ToFile(
          imageUrl,
          new Date().getTime() + '.png',
          'image/png'
        );

        imageUrlFinal.push(convertedFile);
      } else if (file) {
        imageUrlFinal.push(file);
      }

      if (imageUrlFinal.length) {
        const sub = this.companyService
          .uploadLogo(this.selectedCompany._id, imageUrlFinal[0])
          .subscribe(
            (updatedCompany: any) => {
              this.selectedCompany.logoUrl = updatedCompany.logoUrl;
              //    console.log('uploadedAvatar', JSON.stringify(uploadedAvatar));
              // this.sessionManager.setCurrentUser(userUpdated);
              // this.store.dispatch(new DisableSettingsEdit());
              (event.target as HTMLInputElement).value = '';
            },
            (error) => {
              //    console.log('Error', error);
              if (error?.error?.message) {
                const errorsList = error?.error?.message.split('.');
                const errorDescription =
                  'Update Error\n\nSome fields are invalid...\nPlease check fields:\n\n';

                const fullMessage = errorDescription + errorsList.join('.\n');
                window.alert(fullMessage);
              }
              (event.target as HTMLInputElement).value = '';
            }
          );
        this.subs.push(sub);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.backgroundImageUrl = reader.result; // Store the file URL to use it as a background
        };
        reader.readAsDataURL(imageUrlFinal[0]); // Read the file as a data URL
      }
      (event.target as HTMLInputElement).value = '';
    }
  }

  updateDateOfEstablishment(date: Date) {
    this.selectedCompany.dateOfEstablishment = date ?? null;
  }

  onAvatarChange(_event?: MouseEvent) {
    this.element.nativeElement.getElementsByClassName('file-input')[0].click();
  }

  onSelectChange(selectValue: string, field?: string): void {
    this.selectedCompany[field] = selectValue;
  }

  selectedSignatureFile(fileUrl: string) {
    this.selectedCompany.signatureUrl = fileUrl;
  }

  selectedCompanyStamp(fileUrl: string) {
    this.selectedCompany.sealUrl = fileUrl;
  }
  onInputChange(newValue: string, field?: string): void {
    this.selectedCompany[field] = newValue;
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }
}
